import type { MouseEvent } from 'react';

import { ExternalLink } from '@uikit/components/ExternalLink';
import { Spinner } from '@uikit/components/Spinner';
import { AngleRight } from '@uikit/icons/AngleRight';
import { ArrowRight } from '@uikit/icons/ArrowRight';
import { Checked3 } from '@uikit/icons/Checked3';

import { useAuth } from '@shared/common/providers/AuthProvider';
import useCheckRequiredSelfData from '@shared/hooks/useCheckRequiredSelfData';
import useSelfDataCheckLocalStorage from '@shared/hooks/useSelfDataCheckLocalStorage';

import { JOIN_CAMPAIGN_STEP_TYPE } from '../getStepsToJoinCampaign';
import type { StepsToJoinCampaignStep } from '../StepsToJoinCampaign';
import { JoinTurtleClubActionButton } from './JoinTurtleClubActionButton';
import { StartEarningWithTurtleClubActionButton } from './StartEarningWithTurtleClubActionButton';

const DEFAULT_AUTH_URLS = {
  [JOIN_CAMPAIGN_STEP_TYPE.REGISTER_MAGIC_STORE_ACCOUNT]: '/store',
  [JOIN_CAMPAIGN_STEP_TYPE.ADD_EVM_WALLET]: '/store/profile/settings',
  [JOIN_CAMPAIGN_STEP_TYPE.USERNAME_AND_EMAIL]: '/store/profile/settings?fillProfile=true',
  [JOIN_CAMPAIGN_STEP_TYPE.SIGNUP_AND_VERIFY_EMAIL]: '/store/profile/settings?fillProfile=true',
  [JOIN_CAMPAIGN_STEP_TYPE.LINK_TON_WALLET]: '/store/profile/settings',
  [JOIN_CAMPAIGN_STEP_TYPE.LINK_SOLANA_WALLET]: '/store/profile/magic-id/wallets',
  [JOIN_CAMPAIGN_STEP_TYPE.VERIFY_HUMANITY]: '/store/profile/settings',
  [JOIN_CAMPAIGN_STEP_TYPE.VERIFY_HUMANITY_LIVENESS]: '/store/profile/settings',
  [JOIN_CAMPAIGN_STEP_TYPE.LINK_TELEGRAM]: '/store/profile/settings',
} as const;

export const StepActionButton = ({
  step,
  campaignUrl,
}: {
  step: StepsToJoinCampaignStep;
  campaignUrl?: string;
}) => {
  const { isLoading: isAuthLoading } = useAuth();
  const {
    check,
    hasEmail,
    hasEvmWallet,
    hasTonWallet,
    hasSolanaWallet,
    hasUsername,
    isAuth,
    hasFaceVerification,
    hasGitcoinVerification,
    hasLinkedTelegram,
    isHumanityVerificationLoading,
  } = useCheckRequiredSelfData();
  const { value: selfDataCheckLocalStorage } = useSelfDataCheckLocalStorage();

  const isEmailVerified = hasEmail || selfDataCheckLocalStorage.hasEmail;

  const isAddEvmWalletStepVerified =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.ADD_EVM_WALLET &&
    (hasEvmWallet || selfDataCheckLocalStorage.hasEvmWallet);
  const isLinkTonWalletStepVerified =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.LINK_TON_WALLET &&
    (hasTonWallet || selfDataCheckLocalStorage.hasTonWallet);
  const isLinkSolanaWalletStepVerified =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.LINK_SOLANA_WALLET &&
    (hasSolanaWallet || selfDataCheckLocalStorage.hasSolanaWallet);
  const isUsernameAndEmailStepVerified =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.USERNAME_AND_EMAIL &&
    isEmailVerified &&
    (hasUsername || selfDataCheckLocalStorage.hasUsername);
  const isVerifyHumanityStepVerified =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.VERIFY_HUMANITY &&
    (hasFaceVerification ||
      selfDataCheckLocalStorage.hasFaceVerification ||
      hasGitcoinVerification ||
      selfDataCheckLocalStorage.hasGitcoinVerification);
  const isRegisterMagicStoreAccountStepVerified =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.REGISTER_MAGIC_STORE_ACCOUNT && isAuth;
  const isSignedUpAndEmailStepVerified =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.SIGNUP_AND_VERIFY_EMAIL && isAuth && isEmailVerified;
  const isVerifyHUmanityLivenessStepVerified =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.VERIFY_HUMANITY_LIVENESS &&
    (hasFaceVerification || selfDataCheckLocalStorage.hasFaceVerification);

  const isTelegramLinked =
    step.type === JOIN_CAMPAIGN_STEP_TYPE.LINK_TELEGRAM &&
    (hasLinkedTelegram || selfDataCheckLocalStorage.hasLinkedTelegram);

  const isStepVerified =
    isAuth &&
    (isRegisterMagicStoreAccountStepVerified ||
      isAddEvmWalletStepVerified ||
      isLinkTonWalletStepVerified ||
      isLinkSolanaWalletStepVerified ||
      isUsernameAndEmailStepVerified ||
      isVerifyHumanityStepVerified ||
      isVerifyHUmanityLivenessStepVerified ||
      isTelegramLinked ||
      isSignedUpAndEmailStepVerified);

  const handleClick = (event: MouseEvent<HTMLElement>, step: StepsToJoinCampaignStep) => {
    const isAuthRequired = !step.href;

    if (!check({ auth: isAuthRequired })) {
      event.preventDefault();

      return;
    }
  };

  if (!step.type || step.type === JOIN_CAMPAIGN_STEP_TYPE.STORE_TRADE_REWARD) {
    return null;
  }

  if (
    step.type === JOIN_CAMPAIGN_STEP_TYPE.VERIFY_HUMANITY &&
    isHumanityVerificationLoading &&
    !isAuthLoading &&
    isAuth
  ) {
    return (
      <div className="h-8 w-8 md:h-12 md:w-12 flex justify-center items-center">
        <Spinner size="md" color="disabled" />
      </div>
    );
  }

  if (step.type === JOIN_CAMPAIGN_STEP_TYPE.VISIT_CAMPAIGN_PAGE) {
    return (
      <ExternalLink
        href={isAuth ? campaignUrl : '#'}
        onClick={(e) => handleClick(e, step)}
        className="button button-solid-secondary button-md md:button-xl md:min-h-12 rounded-xl gap-2"
      >
        Join Now <ArrowRight className="icon-xs" />
      </ExternalLink>
    );
  }

  if (step.type === JOIN_CAMPAIGN_STEP_TYPE.MONITOR_LEADERBOARD) {
    return (
      <a
        onClick={(e) => handleClick(e, step)}
        href="#leaderboard"
        className="icon-button button-text-primary rotate-90"
      >
        <AngleRight className="icon-lg" />
      </a>
    );
  }

  if (step.type === JOIN_CAMPAIGN_STEP_TYPE.JOIN_TURTLE_CLUB) {
    return <JoinTurtleClubActionButton />;
  }

  if (step.type === JOIN_CAMPAIGN_STEP_TYPE.START_EARNING_WITH_TURTLE_CLUB) {
    return <StartEarningWithTurtleClubActionButton />;
  }

  if (isStepVerified) {
    return (
      <div className="h-8 w-8 md:h-12 md:w-12 flex justify-center items-center">
        <Checked3 className="icon-lg text-green-700" />
      </div>
    );
  }

  // TODO: rework to make actions on the current page
  const defaultUrls = isAuth ? DEFAULT_AUTH_URLS[step.type as keyof typeof DEFAULT_AUTH_URLS] : '#';
  const url = step.href ?? defaultUrls;

  return (
    <a
      href={url}
      onClick={(e) => handleClick(e, step)}
      target="_blank"
      rel="noreferrer"
      className="button button-solid-secondary button-md md:button-xl md:min-h-12 rounded-xl gap-2"
    >
      {step.buttonLabel ?? 'Verify'} <ArrowRight className="icon-xs" />
    </a>
  );
};
